import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

export interface ImpactItem {
  icon: string;
  title: string;
  description: string;
}

@Component({
  selector: 'app-role-delete-impact',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './role-delete-impact.component.html',
  styleUrl: './role-delete-impact.component.css',
})
export class RoleDeleteImpactComponent implements OnInit, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Input() activeModalContext: any;

  items: ImpactItem[] = [];

  ngOnInit(): void {
    this.initItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields'] || changes['activeModalContext']) {
      this.initItems();
    }
  }

  get title(): string {
    const customTitle =
      this.fields?.displayName ||
      this.fields?.title ||
      this.fields?.uiOptions?.title;
    return customTitle ? this.resolvePlaceholders(customTitle) : 'This will affect:';
  }

  initItems(): void {
    if (Array.isArray(this.fields?.items) && this.fields.items.length > 0) {
      this.items = this.fields.items.map((item: any) => ({
        icon: item.icon || 'shield',
        title: this.resolvePlaceholders(item.title || ''),
        description: this.resolvePlaceholders(item.description || ''),
      }));
      return;
    }

    const defaultRole =
      this.fields?.defaultRole ||
      this.activeModalContext?.defaultRole ||
      'SecOps';

    const defaultRoleDisplay = defaultRole.toLowerCase().includes('role')
      ? defaultRole
      : `${defaultRole} role`;

    const firstTitle =
      this.fields?.firstStatementTitle ||
      this.fields?.uiOptions?.firstStatementTitle ||
      'Users assigned to this role';

    const firstDesc =
      this.fields?.firstStatementDescription ||
      this.fields?.uiOptions?.firstStatementDescription ||
      `If this role is deleted, users assigned to it will be reassigned to the default ${defaultRoleDisplay}.`;

    const secondTitle =
      this.fields?.secondStatementTitle ||
      this.fields?.uiOptions?.secondStatementTitle ||
      'Permissions and access rights';

    const secondDesc =
      this.fields?.secondStatementDescription ||
      this.fields?.uiOptions?.secondStatementDescription ||
      'All module, feature and data access configured in this role will be removed.';

    const thirdTitle =
      this.fields?.thirdStatementTitle ||
      this.fields?.uiOptions?.thirdStatementTitle ||
      'Role-based configurations';

    const thirdDesc =
      this.fields?.thirdStatementDescription ||
      this.fields?.uiOptions?.thirdStatementDescription ||
      'Any policies, approval workflows or settings linked to this role will no longer apply.';

    this.items = [
      {
        icon: 'users',
        title: this.resolvePlaceholders(firstTitle),
        description: this.resolvePlaceholders(firstDesc),
      },
      {
        icon: 'shield',
        title: this.resolvePlaceholders(secondTitle),
        description: this.resolvePlaceholders(secondDesc),
      },
      {
        icon: 'document',
        title: this.resolvePlaceholders(thirdTitle),
        description: this.resolvePlaceholders(thirdDesc),
      },
    ];
  }

  resolvePlaceholders(text: string): string {
    if (!text) return '';
    const placeholderRegex = /{{(.*?)}}/g;
    return text.replace(placeholderRegex, (_match, key) => {
      key = key.trim();
      if ((this as any)[key] !== undefined) {
        return (this as any)[key];
      }
      if (this.activeModalContext && this.activeModalContext[key] !== undefined) {
        return this.activeModalContext[key];
      }
      if (this.fields && this.fields[key] !== undefined) {
        return this.fields[key];
      }
      return _match;
    });
  }
}
